/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react'
import axios from 'axios';
import { jwtDecode } from "jwt-decode";
import Navbar from "../Navbar";
import Header from "../Header";
import baseUrl from "../BaseUrl";
import UserSegmentToggle from "../common/UserSegmentToggle";
import { useUserSegment, watchFilterValue } from "../../context/UserSegmentContext";
import Loader from "../Loader";
import { useNavigate, Link, useLocation } from 'react-router-dom';
import Pagination from "react-js-pagination";
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import ColumnsMenu from './ColumnsMenu';
import UsersSummaryCards from './UsersSummaryCards';
import UsersTable from './UsersTable';
import FilterChips from './FilterChips';
import SavedViewsMenu from './SavedViewsMenu';
import ExportButton from './ExportButton';
import BulkActionBar from './BulkActionBar';
import DeleteConfirm from './DeleteConfirm';
import GrantAccessModal from '../free-grants/GrantAccessModal';
import createGrantApi, { grantedMessage } from '../free-grants/grantApi';
import EmptyState from './states/EmptyState';
import ErrorState from './states/ErrorState';
import { readSavedFilters, usePersistFilters } from "../../hooks/filterPersistence";
import '../active-users/active-users.css';   // shared au-btn button styles (match other pages)
import './users-redesign.css';
import '../free-grants/free-grants.css';

const SUB_LABELS = { active: 'Active', trial: 'Trial', cancelled: 'Cancelled', free: 'Free' };
// 088: Dashboard Type (users.dashboard_layout). Standard is the complement of POTS, so it
// also covers members who have never reported a layout — see utills/dashboardTypeFilter.js.
const DASHBOARD_LABELS = { pots: 'POTS', standard: 'Standard' };
const STATE_KEY = "usersListState";
// Saved views are hidden at the client's request (2026-09-29): applying a saved view was not
// working reliably for them. The menu, its handlers and the API are all left in place — flip
// this to true to bring it back; nothing else has to change.
const SHOW_SAVED_VIEWS = false;

const UserList = () => {

    const location = useLocation();
    const abx = location.state;
    const [name, setName] = useState('');
    const [token, setToken] = useState('');
    const [expire, setExpire] = useState('');
    const [popupOpen, setPopupOpen] = useState(false);
    // Free access grant (065): the member whose grant modal is open, and the last outcome banner.
    const [grantUser, setGrantUser] = useState(null);
    const [grantNotice, setGrantNotice] = useState('');
    const [users, setData] = useState([]);
    const [totalItem, setTotalItem] = useState(0);
    const [page, setPage] = useState(1);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [displayFromDate, setDisplayFromDate] = useState("");
    const [displayToDate, setDisplayToDate] = useState("");
    const [selectedDateFilter, setSelectedDateFilter] = useState(abx != null ? abx.selectedDateFilter : "");
    const [selectedPlatformFilter, setSelectedPlatformFilter] = useState(abx != null ? abx.selectedPlatformFilter : "");
    const [selectedSubscriptionFilter, setSelectedSubscriptionFilter] = useState("");
    const [selectedDashboardFilter, setSelectedDashboardFilter] = useState("");
    const [searchValue, setSearchValue] = useState('');

    const [sortField, setSortField] = useState("created_at");
    const [sortOrder, setSortOrder] = useState("DESC");

    const [statastics, setStatastics] = useState(0);
    const [savedViews, setSavedViews] = useState([]);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [selectAllMatching, setSelectAllMatching] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteBusy, setDeleteBusy] = useState(false);

    // Profile thumbnails: fetched as blob URLs (Bearer-authenticated) keyed by
    // user id. Only users with a stored thumbnail are fetched; the rest fall
    // back to the initials avatar. Kept in a ref too so we can revoke on reload.
    const [avatarUrls, setAvatarUrls] = useState({});
    const avatarUrlsRef = useRef({});

    // Show ALL columns by default; the compact Columns menu can hide any of them.
    const [columnVisibility, setColumnVisibility] = useState({
        name: true, watch_name: true, data_source: true, platform: true,
        mobile_app_version: true, watch_app_version: true, plan_info: true, subscription: true,
        last_seen: true, created_at: true, dashboard_layout: true, managing_condition: true,
        access_count: true, active_last_30_days: true, alert_count: true, new_open: true, resume: true,
    });
    const toggleColumn = (key) => setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));

    const COLUMN_LABELS = {
        name: 'User', watch_name: 'watch name', data_source: 'data source',
        platform: 'platform', mobile_app_version: 'mobile app version', watch_app_version: 'watch app version',
        plan_info: 'subscription', subscription: 'plan', last_seen: 'last active (UTC)', created_at: 'registered date',
        dashboard_layout: 'dashboard layout', managing_condition: 'managing condition',
        access_count: 'activity count', active_last_30_days: 'active in last 30 days', alert_count: 'alerts',
        new_open: 'new open', resume: 'resume',
    };
    const columnOptions = Object.keys(columnVisibility).map((key) => ({ value: key, label: COLUMN_LABELS[key] || key.replace(/_/g, ' ') }));

    const navigate = useNavigate();
    const { segment } = useUserSegment();   // global watch/without/all segment (feature 023)
    // Wire the segment to the existing get-user `watch_filter` param.
    const watchValue = watchFilterValue(segment);

    const axiosJWT = axios.create();
    axiosJWT.interceptors.request.use(async (config) => {
        const currentDate = new Date();
        if (expire * 1000 < currentDate.getTime()) {
            const response = await axios.get(`${baseUrl}/admin/api/token`);
            config.headers.Authorization = `Bearer ${response.data.accessToken}`;
            setToken(response.data.accessToken);
            const decoded = jwtDecode(response.data.accessToken);
            setExpire(decoded.exp);
        }
        return config;
    }, (error) => Promise.reject(error));

    // The modal's server adapter — shared with User 360 and the two survey review tabs, all of
    // which open the same modal (see free-grants/grantApi.js).
    const grantApi = createGrantApi(axiosJWT, token, baseUrl);

    const handleGranted = (grant) => {
        setGrantUser(null);
        // billing_message carries the part an admin needs to act on — a failed store call still
        // means the member HAS their access, and that distinction has to survive to the screen.
        setGrantNotice(grantedMessage(grant));
        // Refetch so the row's subscription pill and its "Last provided" date show the grant that
        // was just made — both are server-derived, so nothing on screen is right until we reload.
        fetchUsers();
    };

    useEffect(() => { refreshToken(); }, []);
    useEffect(() => {
        setName('Pauser');
        // Restore the list view on return from a drill-down (Back). An explicit
        // pinned entry (location.state) wins, so only restore when there's none.
        const saved = abx == null ? readSavedFilters(STATE_KEY) : null;
        if (saved) {
            setFromDate(saved.fromDate || ''); setToDate(saved.toDate || '');
            setDisplayFromDate(saved.fromDate ? new Date(saved.fromDate) : '');
            setDisplayToDate(saved.toDate ? new Date(saved.toDate) : '');
            setSelectedDateFilter(saved.selectedDateFilter || '');
            setSelectedPlatformFilter(saved.selectedPlatformFilter || '');
            setSelectedSubscriptionFilter(saved.selectedSubscriptionFilter || '');
            setSelectedDashboardFilter(saved.selectedDashboardFilter || '');
            setSearchValue(saved.searchValue || '');
            setSortField(saved.sortField || 'created_at');
            setSortOrder(saved.sortOrder || 'DESC');
            setPage(saved.page || 1);
            fetchUsers({
                page: saved.page || 1, fromDate: saved.fromDate || '', toDate: saved.toDate || '',
                dateFilter: saved.selectedDateFilter || '', platform: saved.selectedPlatformFilter || '',
                subscriptionState: saved.selectedSubscriptionFilter || '',
                dashboardType: saved.selectedDashboardFilter || '', search: saved.searchValue || '',
                sortField: saved.sortField || 'created_at', sortOrder: saved.sortOrder || 'DESC',
            });
        } else {
            fetchUsers();
        }
        if (SHOW_SAVED_VIEWS) fetchSavedViews();
    }, []);

    // Snapshot filters so the exact view is restored on Back (remount).
    usePersistFilters(STATE_KEY, {
        fromDate, toDate, selectedDateFilter, selectedPlatformFilter, selectedSubscriptionFilter,
        selectedDashboardFilter, searchValue, sortField, sortOrder, page,
    });

    // Re-fetch when the global watch/all segment changes (skip initial mount).
    const didMountSegment = useRef(false);
    useEffect(() => {
        if (!didMountSegment.current) { didMountSegment.current = true; return; }
        setPage(1);
        fetchUsers({ page: 1, watch: watchValue });
    }, [segment]);

    const refreshToken = async () => {
        try {
            const response = await axios.get(`${baseUrl}/admin/api/token`);
            setToken(response.data.accessToken);
            const decoded = jwtDecode(response.data.accessToken);
            setExpire(decoded.exp);
        } catch (err) {
            if (err.response) { navigate("/"); window.location.reload(); }
        }
    }

    // Current filter snapshot; overrides win so callers avoid stale-state races.
    const filterState = () => ({
        page, fromDate, toDate, dateFilter: selectedDateFilter, platform: selectedPlatformFilter,
        watch: watchValue, subscriptionState: selectedSubscriptionFilter,
        dashboardType: selectedDashboardFilter, search: searchValue,
        sortField, sortOrder,
    });

    const fetchUsers = async (overrides = {}) => {
        const f = { ...filterState(), ...overrides };
        setPopupOpen(true);
        setError(false);
        setSelectedIds(new Set());
        setSelectAllMatching(false);
        try {
            const url = `${baseUrl}/admin/api/get-user?page=${f.page}&limit=20`
                + `&from_date=${f.fromDate}&to_date=${f.toDate}&date_filter=${f.dateFilter}`
                + `&platform=${f.platform}&watch_filter=${f.watch}&subscription_state=${f.subscriptionState}`
                + `&dashboard_type=${f.dashboardType}`
                + `&search=${f.search}&sortField=${f.sortField}&sortOrder=${f.sortOrder}`;
            const response = await axiosJWT.get(url, { headers: { Authorization: `Bearer ${token}` } });
            setData(response.data.data);
            setStatastics(response.data.statastics);
            setTotalItem(response.data.totalItem);
            loadAvatars(response.data.data);
        } catch (err) {
            setError(true);
            setData([]);
        } finally {
            setPopupOpen(false);
            setLoaded(true);
        }
    };

    // Revoke every blob URL we created and reset the map (called before a fresh
    // page load and on unmount to avoid leaking object URLs).
    const revokeAvatars = () => {
        Object.values(avatarUrlsRef.current).forEach((u) => {
            try { URL.revokeObjectURL(u); } catch (e) { /* ignore */ }
        });
        avatarUrlsRef.current = {};
    };

    // Fetch a thumbnail blob (Bearer-auth) for each user that has one and expose
    // it as an object URL. Failures are silent — the row keeps its initials avatar.
    const loadAvatars = (list) => {
        revokeAvatars();
        setAvatarUrls({});
        (list || [])
            .filter((u) => u && u.id != null && u.profile_thumbnail_path)
            .forEach(async (u) => {
                try {
                    const res = await axiosJWT.get(`${baseUrl}/admin/api/users/${u.id}/thumbnail`, {
                        headers: { Authorization: `Bearer ${token}` },
                        responseType: 'blob',
                    });
                    const objectUrl = URL.createObjectURL(res.data);
                    avatarUrlsRef.current[u.id] = objectUrl;
                    setAvatarUrls((prev) => ({ ...prev, [u.id]: objectUrl }));
                } catch (err) { /* no thumbnail / fetch failed → initials fallback */ }
            });
    };

    // Revoke any outstanding object URLs when the page unmounts.
    useEffect(() => () => revokeAvatars(), []);

    const fetchSavedViews = async () => {
        try {
            const res = await axiosJWT.get(`${baseUrl}/admin/api/users/saved-views`, { headers: { Authorization: `Bearer ${token}` } });
            if (Array.isArray(res.data?.data)) setSavedViews(res.data.data);
        } catch (err) { /* non-fatal: views are optional */ }
    };

    const handlePageChange = (pageNumber) => { setPage(pageNumber); fetchUsers({ page: pageNumber }); };

    const handleFromDate = (date) => {
        if (!date) { setFromDate(''); setDisplayFromDate(''); return; }
        const f = date.getFullYear() + '-' + (date.getMonth() + 1).toString().padStart(2, '0') + '-' + date.getDate().toString().padStart(2, '0');
        setFromDate(f); setDisplayFromDate(date);
    };
    const handleToDate = (date) => {
        if (!date) { setToDate(''); setDisplayToDate(''); return; }
        const f = date.getFullYear() + '-' + (date.getMonth() + 1).toString().padStart(2, '0') + '-' + date.getDate().toString().padStart(2, '0');
        setToDate(f); setDisplayToDate(date);
    };

    const handleSearchSubmit = () => { setPage(1); fetchUsers({ page: 1 }); };
    const handleSearchChange = (e) => setSearchValue(e.target.value);
    const handleDateFilterChange = (e) => setSelectedDateFilter(e.target.value);
    const handlePlatformFilterChange = (e) => setSelectedPlatformFilter(e.target.value);
    const handleSubscriptionFilterChange = (e) => setSelectedSubscriptionFilter(e.target.value);
    const handleDashboardFilterChange = (e) => setSelectedDashboardFilter(e.target.value);

    const handleSort = (field) => {
        const order = sortField === field && sortOrder === "ASC" ? "DESC" : "ASC";
        setSortField(field); setSortOrder(order); setPage(1);
        fetchUsers({ sortField: field, sortOrder: order, page: 1 });
    };

    // ── Active filter chips ────────────────────────────────────────────────
    const activeFilters = [];
    if (searchValue) activeFilters.push({ key: 'search', label: `Search: "${searchValue}"` });
    if (selectedPlatformFilter) activeFilters.push({ key: 'platform', label: `Platform: ${selectedPlatformFilter}` });
    if (selectedSubscriptionFilter) activeFilters.push({ key: 'subscription', label: `Plan: ${SUB_LABELS[selectedSubscriptionFilter] || selectedSubscriptionFilter}` });
    if (selectedDashboardFilter) activeFilters.push({ key: 'dashboard', label: `Dashboard: ${DASHBOARD_LABELS[selectedDashboardFilter] || selectedDashboardFilter}` });
    if (fromDate || toDate) activeFilters.push({ key: 'date', label: `${selectedDateFilter === '2' ? 'Last seen (UTC)' : 'Created'}: ${fromDate || '…'} – ${toDate || '…'}` });

    const removeFilter = (key) => {
        const ov = { page: 1 };
        setPage(1);
        if (key === 'search') { setSearchValue(''); ov.search = ''; }
        if (key === 'platform') { setSelectedPlatformFilter(''); ov.platform = ''; }
        if (key === 'subscription') { setSelectedSubscriptionFilter(''); ov.subscriptionState = ''; }
        if (key === 'dashboard') { setSelectedDashboardFilter(''); ov.dashboardType = ''; }
        if (key === 'date') {
            setFromDate(''); setToDate(''); setDisplayFromDate(''); setDisplayToDate('');
            ov.fromDate = ''; ov.toDate = '';
        }
        fetchUsers(ov);
    };

    const clearFilter = () => {
        setFromDate(''); setDisplayFromDate(''); setToDate(''); setDisplayToDate('');
        setSelectedDateFilter(''); setSelectedPlatformFilter('');
        setSelectedSubscriptionFilter(''); setSelectedDashboardFilter('');
        setSearchValue(''); setSortField(''); setSortOrder(''); setPage(1);
        fetchUsers({ page: 1, fromDate: '', toDate: '', dateFilter: '', platform: '', subscriptionState: '', dashboardType: '', search: '', sortField: '', sortOrder: '' });
    };

    // ── Saved views ────────────────────────────────────────────────────────
    const applyView = (view) => {
        const fl = view.filters || {};
        setSearchValue(fl.search || '');
        setSelectedPlatformFilter(fl.platform || '');
        setSelectedSubscriptionFilter(fl.subscriptionState || '');
        setSelectedDashboardFilter(fl.dashboardType || '');
        setSelectedDateFilter(fl.dateFilter || '');
        setFromDate(fl.fromDate || ''); setToDate(fl.toDate || '');
        setPage(1);
        // watch segment is governed by the global toggle, not saved views.
        fetchUsers({ page: 1, search: fl.search || '', platform: fl.platform || '', subscriptionState: fl.subscriptionState || '', dashboardType: fl.dashboardType || '', dateFilter: fl.dateFilter || '', fromDate: fl.fromDate || '', toDate: fl.toDate || '' });
    };

    const saveCurrentView = async (viewName) => {
        try {
            await axiosJWT.post(`${baseUrl}/admin/api/users/saved-views`,
                { name: viewName, filters: { search: searchValue, platform: selectedPlatformFilter, watch: watchValue, subscriptionState: selectedSubscriptionFilter, dashboardType: selectedDashboardFilter, dateFilter: selectedDateFilter, fromDate, toDate } },
                { headers: { Authorization: `Bearer ${token}` } });
            fetchSavedViews();
        } catch (err) { /* surfaced by a future toast; ignore for now */ }
    };

    // ── CSV export (filter-based, or by explicit ids) ──────────────────────
    const buildExportUrl = (ids) => {
        if (ids && ids.length) return `${baseUrl}/admin/api/get-user-export?ids=${ids.join(',')}`;
        const f = filterState();
        return `${baseUrl}/admin/api/get-user-export?`
            + `from_date=${f.fromDate}&to_date=${f.toDate}&date_filter=${f.dateFilter}`
            + `&platform=${f.platform}&watch_filter=${f.watch}&subscription_state=${f.subscriptionState}`
            + `&dashboard_type=${f.dashboardType}`
            + `&search=${f.search}`;
    };
    const downloadCsv = async (url) => {
        const res = await axiosJWT.get(url, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
        const href = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
        const a = document.createElement('a');
        a.href = href; a.download = 'users-export.csv';
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(href);
    };
    const exportCsv = () => downloadCsv(buildExportUrl(null)); // toolbar: whole filtered set

    // ── Selection ──────────────────────────────────────────────────────────
    const toggleRow = (id) => {
        setSelectAllMatching(false);
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const togglePage = (pageUsers) => {
        setSelectAllMatching(false);
        setSelectedIds((prev) => {
            const allSelected = pageUsers.length > 0 && pageUsers.every((u) => prev.has(u.id));
            if (allSelected) return new Set();
            return new Set(pageUsers.map((u) => u.id));
        });
    };
    const clearSelection = () => { setSelectedIds(new Set()); setSelectAllMatching(false); };
    const pageFullySelected = users.length > 0 && users.every((u) => selectedIds.has(u.id));

    // ── Bulk actions ───────────────────────────────────────────────────────
    const bulkExport = () => downloadCsv(buildExportUrl(selectAllMatching ? null : [...selectedIds]));

    const openDelete = () => { if (selectedIds.size) setDeleteOpen(true); };
    const confirmDelete = async () => {
        const emails = users.filter((u) => selectedIds.has(u.id)).map((u) => u.email).filter(Boolean);
        if (!emails.length) { setDeleteOpen(false); return; }
        setDeleteBusy(true);
        try {
            await axiosJWT.post(`${baseUrl}/admin/api/hard-delete-user`, { emails }, { headers: { Authorization: `Bearer ${token}` } });
            setDeleteOpen(false);
            fetchUsers();
        } catch (err) { /* future toast */ } finally {
            setDeleteBusy(false);
        }
    };

    const deleteView = async (id) => {
        try {
            await axiosJWT.delete(`${baseUrl}/admin/api/users/saved-views/${id}`, { headers: { Authorization: `Bearer ${token}` } });
            fetchSavedViews();
        } catch (err) { /* ignore */ }
    };

    const hasActiveFilters = activeFilters.length > 0;
    const showEmpty = loaded && !popupOpen && !error && users.length === 0;

    return (
        <div className="panelbox">
            <Navbar />
            <div className="right-panel">
                <Header name={name} />
                {popupOpen && (<Loader />)}
                <div className="content-page">
                    <div className="row uv-head">
                        <div className="col-12 col-lg-6"><div className="left-title"><h1>Users</h1></div></div>
                        <div className="col-12 col-lg-6">
                            <div className="uv-head-right">
                                <UserSegmentToggle label="" />
                                <span className="au-back"><Link to="/dashboard">← Back</Link></span>
                            </div>
                        </div>
                    </div>

                    <UsersSummaryCards statastics={statastics} totalItem={totalItem} />

                    <div className="uv-toolbar">
                        <div className="boxinput">
                            {/* 061: the box searches every text column the table can show.
                                The full list will not fit a placeholder, so the summary goes
                                in the placeholder and the complete set in the tooltip. */}
                            <input className="search-leave satff-listing-page-search" type="text" name="search"
                                placeholder="🔍  Search any user detail — name, condition, layout…"
                                title="Searches name, email, country, watch name, data source, platform, mobile app version, watch app version, dashboard layout and managing condition"
                                value={searchValue} onChange={handleSearchChange} onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()} />
                            <select className="form-control" name="dateFilter" value={selectedDateFilter} onChange={handleDateFilterChange}>
                                <option value="1">Created</option>
                                <option value="2">Last Seen (UTC)</option>
                            </select>
                            <select className="form-control" name="platformFilter" value={selectedPlatformFilter} onChange={handlePlatformFilterChange}>
                                <option value="">Platform</option>
                                <option value="Android">Android</option>
                                <option value="iOS">iOS</option>
                            </select>
                            <select className="form-control" name="subscriptionFilter" value={selectedSubscriptionFilter} onChange={handleSubscriptionFilterChange}>
                                <option value="">Plan</option>
                                <option value="active">Active</option>
                                <option value="trial">Trial</option>
                                <option value="cancelled">Cancelled</option>
                                <option value="free">Free</option>
                            </select>
                            <select className="form-control" name="dashboardFilter" aria-label="Dashboard type filter"
                                title="The dashboard the member uses in the app. Standard covers everyone who is not on the POTS dashboard, including members who have never reported one."
                                value={selectedDashboardFilter} onChange={handleDashboardFilterChange}>
                                <option value="">Dashboard Type</option>
                                <option value="pots">POTS</option>
                                <option value="standard">Standard</option>
                            </select>
                            <DatePicker selected={displayFromDate} onChange={handleFromDate} dateFormat="dd/MM/yyyy" placeholderText="From Date" className="from-date" wrapperClassName="uv-date" />
                            <DatePicker selected={displayToDate} onChange={handleToDate} dateFormat="dd/MM/yyyy" placeholderText="To Date" className="end-date" wrapperClassName="uv-date" />
                            <div className="uv-filter-actions">
                                <button type="button" className="au-btn au-btn--primary" onClick={handleSearchSubmit}>Search</button>
                                {hasActiveFilters && (
                                    <button type="button" className="au-btn au-btn--ghost" data-testid="clear-filter" onClick={clearFilter}>Clear</button>
                                )}
                            </div>
                        </div>
                        <div className="uv-toolbar-secondary">
                            {SHOW_SAVED_VIEWS && (
                                <div className="uv-views-wrap">
                                    <SavedViewsMenu views={savedViews} onApply={applyView} onSave={saveCurrentView} onDelete={deleteView} />
                                </div>
                            )}
                            {/* Export and Columns act on the table below, so they sit together on the right. */}
                            <div className="uv-toolbar-end">
                                <ExportButton onExport={exportCsv} />
                                <ColumnsMenu options={columnOptions} visibility={columnVisibility} onToggle={toggleColumn} />
                            </div>
                        </div>
                    </div>

                    <FilterChips filters={activeFilters} onRemove={removeFilter} onClearAll={clearFilter} resultCount={totalItem} />

                    <BulkActionBar
                        count={selectedIds.size}
                        totalMatching={totalItem}
                        selectAllMatching={selectAllMatching}
                        pageFullySelected={pageFullySelected}
                        onSelectAllMatching={() => setSelectAllMatching(true)}
                        onClear={clearSelection}
                        onExport={bulkExport}
                        onDelete={openDelete}
                        canDelete={false}
                    />

                    {grantNotice && (
                        <div className="fg-alert fg-alert--ok" role="status" style={{ marginBottom: 12 }}>
                            {grantNotice}
                            <button
                                type="button"
                                className="fg-btn"
                                style={{ marginLeft: 12 }}
                                onClick={() => setGrantNotice('')}
                            >
                                Dismiss
                            </button>
                        </div>
                    )}

                    <div className="row">
                        <div className="col-12">
                            {error ? (
                                <ErrorState onRetry={handleSearchSubmit} />
                            ) : showEmpty ? (
                                <EmptyState hasFilters={hasActiveFilters} onClear={clearFilter} />
                            ) : (
                                <>
                                    <UsersTable
                                        users={users}
                                        avatarUrls={avatarUrls}
                                        onGrantAccess={setGrantUser}
                                        columnVisibility={columnVisibility}
                                        sortField={sortField}
                                        sortOrder={sortOrder}
                                        onSort={handleSort}
                                        selectable
                                        selectedIds={selectedIds}
                                        onToggleRow={toggleRow}
                                        onTogglePage={togglePage}
                                    />
                                    {totalItem > 20 && (
                                        <Pagination activePage={page} itemsCountPerPage={20} totalItemsCount={totalItem} pageRangeDisplayed={5} onChange={handlePageChange.bind(this)} />
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {deleteOpen && (
                <DeleteConfirm
                    count={selectedIds.size}
                    busy={deleteBusy}
                    onConfirm={confirmDelete}
                    onCancel={() => setDeleteOpen(false)}
                />
            )}
            {grantUser && (
                <GrantAccessModal
                    user={grantUser}
                    api={grantApi}
                    onClose={() => setGrantUser(null)}
                    onGranted={handleGranted}
                />
            )}
        </div>
    )
}

export default UserList
